import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, CheckCircle2 } from "lucide-react";
import { http, apiError } from "@/lib/api";
import { Wordmark } from "@/components/Shell";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!token) { setErr("Missing reset token"); return; }
    if (pw.length < 8) { setErr("Password must be at least 8 characters"); return; }
    if (pw !== confirm) { setErr("Passwords do not match"); return; }
    setBusy(true);
    try {
      await http.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (e2) { setErr(apiError(e2.response?.data?.detail) || "Could not reset password"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#FBFBFB] text-[#323232] px-4">
      <div className="h-1 w-full absolute top-0 left-0" style={{ background: "linear-gradient(90deg,#A5300F 0%,#D55816 45%,#E39629 100%)" }} />
      <div className="mb-6"><Wordmark /></div>
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#ECE6DD] shadow-[0_18px_50px_-24px_rgba(50,50,50,0.35)] overflow-hidden" data-testid="reset-password-card">
        <div className="h-[3px] w-full" style={{ background: "linear-gradient(90deg,#A5300F,#E39629)" }} />
        <div className="p-8">
          {done ? (
            <div className="text-center py-6" data-testid="reset-done">
              <CheckCircle2 className="w-10 h-10 text-[#2E7D32] mx-auto" />
              <p className="mt-3 font-semibold">Password reset. Redirecting to sign-in…</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-bold text-[#A5300F] mb-3"><KeyRound className="w-3.5 h-3.5" /> Reset password</div>
              <h2 className="font-display text-2xl font-bold">Choose a new password</h2>
              <p className="text-sm text-[#8A8175] mt-1 mb-5">Set a new password for your CyberLens account.</p>
              {err && <div className="mb-4 text-sm text-[#A5300F] bg-[#FFF5EB] border border-[#F0DAC2] rounded-lg px-3.5 py-2.5" data-testid="reset-error">{err}</div>}
              <form onSubmit={submit}>
                <label className="text-[11px] uppercase tracking-wider text-[#8A8175] font-bold">New password</label>
                <input type="password" data-testid="reset-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters"
                  className="w-full mt-1 mb-4 border border-[#E7D9C6] rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#A5300F]" />
                <label className="text-[11px] uppercase tracking-wider text-[#8A8175] font-bold">Confirm password</label>
                <input type="password" data-testid="reset-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="w-full mt-1 mb-4 border border-[#E7D9C6] rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#A5300F]" />
                <button type="submit" disabled={busy || !pw || !confirm} data-testid="reset-submit"
                  className="w-full mt-3 bg-[#A5300F] text-white font-semibold py-3 rounded-lg hover:bg-[#8F2A0D] disabled:opacity-50">
                  {busy ? "Resetting…" : "Reset password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
